// scheduling/components/IDDHelpModal.jsx
// How the IDD / Budgets page works, short first: what you're doing, the three steps, and
// THIS cycle's population (read live from the cycle row). The detail sits in collapsible
// sections below. Auto-opens once per browser session; also the "?" button.
import { fmtDate } from './idd/iddFormat';
import { extensionCutoff } from '../utils/iddSteps';

function Population({ cycle, jobCount }) {
  if (!cycle) return null;
  const ps = cycle.PeriodStartFrom || cycle.PeriodStartTo;
  const pe = cycle.PeriodEndFrom || cycle.PeriodEndTo;
  const range = (from, to) => (from && to ? `between ${fmtDate(from)} and ${fmtDate(to)}`
    : from ? `on or after ${fmtDate(from)}` : `on or before ${fmtDate(to)}`);
  return (
    <div className="idd-help-population">
      <div className="idd-help-population-title">Your jobs in {cycle.CycleName}</div>
      <ul>
        <li>
          The jobs you manage{jobCount != null ? <> (<b>{jobCount}</b>)</> : null}
          {pe && <> whose tax year <b>ends {range(cycle.PeriodEndFrom, cycle.PeriodEndTo)}</b></>}
          {ps && <>{pe ? ' and' : ' whose tax year'} <b>starts {range(cycle.PeriodStartFrom, cycle.PeriodStartTo)}</b></>}
          {pe && !ps ? <>, <b>whatever date it started</b> (fiscal years count by the date they end).</> : '.'}
        </li>
        <li>Completed, closed and "not doing this year" jobs are left out.</li>
        {cycle.ScheduleUpTo && (
          <li>AI dates are suggested through <b>{fmtDate(cycle.ScheduleUpTo)}</b>. You can type any date you need.</li>
        )}
        {extensionCutoff(cycle.ScheduleUpTo) && (
          <li>Jobs worked mostly after {fmtDate(cycle.ScheduleUpTo)} last year (likely extensions) get AI dates after it instead,
            finishing no later than they did last year and by <b>{fmtDate(extensionCutoff(cycle.ScheduleUpTo))}</b> at the latest.</li>
        )}
        <li>Missing a job, or seeing one that isn't yours? Check its manager and period dates in Practice Engine.</li>
      </ul>
    </div>
  );
}

export default function IDDHelpModal({ open, onClose, cycle, jobCount }) {
  if (!open) return null;
  return (
    <div className="idd-help-overlay" onClick={onClose}>
      <div className="idd-help-modal" onClick={ev => ev.stopPropagation()}>
        <button className="idd-help-close" onClick={onClose} aria-label="Close help">×</button>
        <h2 className="idd-help-title">Budgets &amp; internal due dates</h2>

        <p className="idd-help-lead">
          For each job you manage, set two things: its <b>budget</b> (hours) and its <b>internal due dates</b> (when the
          work starts and when it's due). Scheduling uses both for the coming season. You get help with each: a
          model suggests budgets for returning 1065, 1120 and 1120-S clients, and Claude suggests dates that spread
          your work across the season.
        </p>

        <div className="idd-help-steps">
          <div className="idd-help-step"><span className="idd-step-num">1</span><div><b>Budgets</b><br />Use the suggestion or keep yours. One click per job, or tick several and <b>Apply recommended</b>.</div></div>
          <div className="idd-help-step"><span className="idd-step-num">2</span><div><b>Dates</b><br />Once every job has a budget: accept Claude's dates, keep the ones you have, or type your own.</div></div>
          <div className="idd-help-step"><span className="idd-step-num">3</span><div><b>Submit</b><br />When every job has both, send it all to Practice Engine.</div></div>
        </div>
        <p className="idd-help-saves">Every click saves. You can stop and come back at any time; <b>Change</b> undoes a decision.</p>

        <Population cycle={cycle} jobCount={jobCount} />

        <details className="idd-help-details">
          <summary>Step 1: Budgets, in detail</summary>
          <ul>
            <li>Each job with a model number gets a label:
              <b> Agrees</b> (your budget is within 2h or 10% of the model, whichever is bigger),
              <b> Near model</b> (inside its wider range, but further off),
              <b> Stale roll-forward</b> (last year's budget copied forward, and the model thinks it's well off),
              <b> Differs from model</b> (set this year, well off the model), or <b>No budget</b>.</li>
            <li><b>Use model</b> splits the model's total across your tasks the way last year's hours were split, in half hours.
              <b> Keep</b> leaves your budget as it is; the optional <b>why?</b> records your reason in one tap.</li>
            <li>Jobs without a model number (1040s, audits, new clients) offer <b>Use last year</b>: last year's actual hours
              (the Last year number), split the same way. It's the first button when it differs from your budget; when the two
              are close, <b>Keep</b> comes first. An <b>Agrees</b> job also offers <b>Use model</b> if you want its exact number.</li>
            <li><b>Enter budget</b>, or clicking any row, opens its tasks: type the remaining hours, then <b>Save</b>.</li>
            <li>A job whose last-year job logged no hours shows <b>Not needed?</b>: it opens <b>Remove job</b> (not doing it this year,
              or close it and don't roll it forward).</li>
            <li>Tick several jobs (the box at the top ticks every job shown) and click <b>Apply recommended</b>: each job
              gets its own first button, whether that's the model, last year or keep. Jobs with nothing to suggest are skipped.
              The small links next to it apply one action to all of them instead.</li>
            <li>The model is a starting point, not a guarantee: on average it beats last year's budget, but any single job can be off. You decide.</li>
          </ul>
        </details>

        <details className="idd-help-details">
          <summary>Step 2: Dates, in detail</summary>
          <ul>
            <li>Claude's dates were made before the cycle opened, from your budgets where you had them and the model's
              (or Practice Engine's) otherwise. Dates unlock when every job has a budget; if any budget changed since the AI dates
              were made, Claude then re-plans them with your numbers by itself (a minute or two). <b>Refresh AI dates</b> runs it again any time.</li>
            <li>They never start before the tax year ends, never before mid-April for a client marked "Post 4/15" in
              Practice Engine, follow last year's order (the jobs worked earliest last year come first), and keep each week's load
              even. A fiscal-year job that can start this fall starts within two weeks of when last year's work began.</li>
            <li><b>Accept AI dates</b> or <b>Keep</b> the dates you have, or type your own and click <b>Save dates</b>.
              Ticking several and <b>Apply recommended</b> accepts the AI dates (or keeps yours where there are none).</li>
            <li>The timeline shows every job as a bar. Hover a row (or a bar) and that job lights up gold in the table, on the
              timeline, and on the hours chart below it (last year's hours for that job). Click a row to keep it lit.</li>
          </ul>
        </details>

        <details className="idd-help-details">
          <summary>Step 3: Submit, in detail</summary>
          <ul>
            <li>Submit is open once every job has a budget and dates (or is removed). It writes them to Practice Engine and
              makes this page read-only for you.</li>
            <li>Need a change afterwards? An admin can reopen your submission without undoing Practice Engine.</li>
          </ul>
        </details>

        <details className="idd-help-details">
          <summary>Saving, undo and history</summary>
          <ul>
            <li>Each decision saves straight away; there's no Save button to remember. <b>Change</b> undoes one decision.
              To undo many, pick <b>Show: Decided</b>, tick them (or the box at the top), and click <b>Undo decisions</b>.</li>
            <li>The <b>⋯</b> menu on a row has <b>History</b> (every change, who made it, and a restore), <b>Remove job</b>
              (not doing it this year, or close it and don't roll it forward), and <b>Clear saved changes</b>.</li>
          </ul>
        </details>

        <details className="idd-help-details">
          <summary>Words used here</summary>
          <ul>
            <li><b>Internal due dates (IDD)</b>: the start and due dates your team works to. Not the filing deadline.</li>
            <li><b>Last year</b>: the same client's job for the prior year.</li>
            <li><b>Hours so far</b>: time already logged on this year's job.</li>
            <li><b>Other / out of scope</b>: time logged without a task; it isn't part of the budget.</li>
            <li><b>Practice Engine</b>: where budgets and dates live once you submit.</li>
          </ul>
        </details>

        <div className="idd-help-footer">
          <button className="idd-btn idd-btn--primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}
